"use client";

import {
  BackgroundImage,
  HeroTitle,
  TypewriterHighlight,
} from "@/components/hero/HeroComponents";
import type { CompanyInfo } from "@/app/api/company-lookup/route";
import EmailInput from "@/components/hero/EmailInput";
import HeroShowcase, { type ShowcaseTab } from "@/components/hero/HeroShowcase";
import { isPersonalDomain } from "@/utils/personalDomains";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Hero = () => {
  const { t, i18n } = useTranslation();
  const titleHighlights = t("hero.titleHighlights", {
    returnObjects: true,
  }) as string[];

  const [activeTab, setActiveTab] = useState<ShowcaseTab>("ai-voice-calls");
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupDomain, setLookupDomain] = useState<string | null>(null);
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const getGenericCompanyInfo = (email: string): CompanyInfo => {
    const localPart = email.split("@")[0] || "";
    const inferredName =
      localPart
        .split(/[._\-+]/)
        .filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .slice(0, 1)
        .join(" ") || null;

    return {
      companyName: t("hero.generic.companyName"),
      industryTag: t("hero.generic.industryTag"),
      headquarters: "",
      companySize: "",
      keyProducts: t("hero.generic.keyProducts"),
      targetMarket: t("hero.generic.targetMarket"),
      gailCanDo: [
        t("hero.generic.gailCanDo.0"),
        t("hero.generic.gailCanDo.1"),
        t("hero.generic.gailCanDo.2"),
      ],
      inferredFirstName: inferredName,
    };
  };

  const handleEmailSubmit = async (email: string) => {
    const domain = email.split("@")[1]?.toLowerCase() || null;
    setLookupDomain(domain);
    setIsLoading(true);
    setError(null);
    setCompanyInfo(null);
    setEmailSubmitted(true);
    setActiveTab("gail-for-you");

    if (domain && isPersonalDomain(domain)) {
      setCompanyInfo(getGenericCompanyInfo(email));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/company-lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, language: i18n.language }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to look up company");
      }

      const data: CompanyInfo = await response.json();
      setCompanyInfo(data);
    } catch (err) {
      console.error("Company lookup error:", err);
      setError(t("hero.lookupError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={clsx("relative", "overflow-hidden")}>
      <BackgroundImage />
      <motion.div
        layout
        className={clsx(
          "relative",
          "flex",
          "flex-col",
          "items-center",
          "text-center",
          "pt-24",
          "pb-8",
          "md:pt-36",
          "md:pb-16",
        )}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Heading */}
        <HeroTitle
          title={t("hero.title")}
          highlight={
            <TypewriterHighlight key={i18n.language} phrases={titleHighlights} />
          }
        />

        <AnimatePresence initial={false}>
          {!emailSubmitted && (
            <motion.div
              key="hero-email-input"
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12, height: 0, marginTop: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="mt-10 md:mt-14 w-full max-w-xl mx-auto px-4"
            >
              <EmailInput
                onSubmit={handleEmailSubmit}
                isLoading={isLoading}
                variant="light"
              />
              {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs + Showcase */}
        <motion.div layout className="w-full">
          <HeroShowcase
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            emailSubmitted={emailSubmitted}
            companyInfo={companyInfo}
            isLoading={isLoading}
            domain={lookupDomain}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Hero;
